package com.dotdrives.web.pages.nextsteps

import com.dotdrives.controller.AutomationRulesController
import com.dotdrives.controller.AutomationRulesUiState
import com.dotdrives.controller.NewReminderController
import com.dotdrives.controller.NewReminderUiState
import com.dotdrives.controller.NextStepsController
import com.dotdrives.controller.NextStepsUiState
import com.dotdrives.controller.PipelineHealthController
import com.dotdrives.controller.PipelineHealthUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.OrgMember
import com.dotdrives.model.ReminderFilter
import com.dotdrives.web.ui.DotButton
import kotlin.time.Clock
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface NextStepsPageProps : Props {
    var canAdminister: Boolean
    var canWriteReminders: Boolean
    var nextStepsController: NextStepsController
    var nextStepsState: NextStepsUiState
    var healthController: PipelineHealthController
    var healthState: PipelineHealthUiState
    var automationController: AutomationRulesController
    var automationState: AutomationRulesUiState
    var newReminderController: NewReminderController
    var newReminderState: NewReminderUiState
    var members: List<OrgMember>
    var currentUserId: String
    var currentUserName: String
}

private val FILTER_TABS = listOf(
    ReminderFilter.All to "All",
    ReminderFilter.Mine to "Mine",
    ReminderFilter.AutoCreated to "⚡ Auto-created",
    ReminderFilter.Recurring to "↻ Recurring",
    ReminderFilter.Done to "Done",
)

val NextStepsPage = FC<NextStepsPageProps> { props ->
    val (now) = useState { Clock.System.now() }
    val (newReminderOpen, setNewReminderOpen) = useState(false)

    useEffect(props.nextStepsController) { props.nextStepsController.load() }
    useEffect(props.healthController) { props.healthController.load() }
    useEffect(props.automationController) { props.automationController.load() }

    useEffect(props.newReminderState.savedReminder?.id) {
        if (props.newReminderState.savedReminder != null) {
            setNewReminderOpen(false)
            props.nextStepsController.load()
            props.newReminderController.start()
        }
    }

    NextStepsStyles()

    div {
        className = ClassName("ns-page")

        div {
            className = ClassName("ns-header")
            div {
                className = ClassName("ns-header-row")
                span { className = ClassName("ns-title"); +"Next steps" }
                div {
                    className = ClassName("ns-header-actions")
                    if (props.canAdminister) DotButton {
                        text = "⚙ Automation rules"
                        variant = DotButtonVariant.Secondary
                        onClick = { props.automationController.openBuilder() }
                    }
                    if (props.canWriteReminders) DotButton {
                        text = "+ New reminder"
                        onClick = {
                            props.newReminderController.start()
                            setNewReminderOpen(true)
                        }
                    }
                }
            }
            div {
                className = ClassName("ns-tabs")
                FILTER_TABS.forEach { (filter, label) ->
                    react.dom.html.ReactHTML.button {
                        type = ButtonType.buttonType
                        className = ClassName("ns-tab" + if (props.nextStepsState.filter == filter) " ns-tab-active" else "")
                        onClick = { props.nextStepsController.setFilter(filter) }
                        +if (filter == ReminderFilter.All && props.nextStepsState.filter == ReminderFilter.All) {
                            "$label · ${props.nextStepsState.reminders.size}"
                        } else {
                            label
                        }
                    }
                }
            }
        }

        div {
            className = ClassName("ns-grid")
            div {
                ReminderList {
                    canWrite = props.canWriteReminders
                    loading = props.nextStepsState.loading
                    reminders = props.nextStepsState.reminders
                    filter = props.nextStepsState.filter
                    error = props.nextStepsState.error
                    members = props.members
                    this.now = now
                    onRetry = props.nextStepsController::retry
                    onComplete = props.nextStepsController::complete
                    onSnooze = props.nextStepsController::snooze
                    onReassign = props.nextStepsController::reassign
                    onNewReminder = {
                        props.newReminderController.start()
                        setNewReminderOpen(true)
                    }
                    onClearFilter = { props.nextStepsController.setFilter(ReminderFilter.All) }
                    onOpenAutomationBuilder = if (props.canAdminister) props.automationController::openBuilder else null
                }
            }
            div {
                PipelineHealthPanel {
                    loading = props.healthState.loading
                    entries = props.healthState.entries
                    error = props.healthState.error
                    onRetry = props.healthController::retry
                }
                if (props.canAdminister) AutomationRulesPanel {
                    loading = props.automationState.loading
                    rules = props.automationState.rules
                    error = props.automationState.error
                    onRetry = props.automationController::retry
                    onToggle = props.automationController::setEnabled
                    onOpenBuilder = props.automationController::openBuilder
                }
            }
        }
    }

    if (props.canWriteReminders && newReminderOpen) {
        NewReminderDialog {
            controller = props.newReminderController
            state = props.newReminderState
            members = props.members
            currentUserId = props.currentUserId
            currentUserName = props.currentUserName
            this.now = now
            onCancel = { setNewReminderOpen(false) }
        }
    }

    if (props.automationState.builderOpen) {
        RuleBuilderDialog {
            draft = props.automationState.draft
            previewCount = props.automationState.previewCount
            previewComplete = props.automationState.previewComplete
            previewLoading = props.automationState.previewLoading
            saving = props.automationState.saving
            builderError = props.automationState.builderError
            onSetName = props.automationController::setDraftName
            onSetMinAmountCents = props.automationController::setDraftMinAmountCents
            onSetDescriptionTemplate = props.automationController::setDraftDescriptionTemplate
            onSetDueInDays = props.automationController::setDraftDueInDays
            onCancel = props.automationController::closeBuilder
            onSave = props.automationController::save
        }
    }
}
